import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

interface EmailPreviewProps {
  subject: string;
  html: string;
}

const PLACEHOLDER_NOTE_RE = /\{\{\s*\d+\s*\}\}/g;

function buildSrcDoc(html: string): string {
  const trimmed = (html || '').trim();
  const body = trimmed
    ? trimmed
    : '<p style="color:#9aa0a6;font-style:italic;font-family:Arial,sans-serif">Your email content will appear here…</p>';

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <base target="_blank" />
    <style>
      html, body { margin: 0; padding: 0; background: #ffffff; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
        color: #202124;
        font-size: 14px;
        line-height: 1.5;
        padding: 16px;
      }
      img { max-width: 100%; height: auto; }
      a { color: #1a73e8; }
    </style>
  </head>
  <body>
    ${body}
  </body>
</html>`;
}

export default function EmailPreview({ subject, html }: EmailPreviewProps) {
  const srcDoc = useMemo(() => buildSrcDoc(html), [html]);
  const trimmedSubject = (subject || '').trim();
  const placeholderCount = (html.match(PLACEHOLDER_NOTE_RE) || []).length;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 480,
        mx: 'auto',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: '#fff',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
      }}
    >
      {/* Inbox-style header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fafafa',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: '#202124', wordBreak: 'break-word' }}
        >
          {trimmedSubject || (
            <Box component="span" sx={{ color: '#9aa0a6', fontStyle: 'italic' }}>
              (No subject)
            </Box>
          )}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: '#1976D2',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            H
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: '#202124' }}>
              Huttons
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#5f6368', display: 'block', lineHeight: 1.2 }}
            >
              to recipient@example.com
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Rendered HTML body */}
      <Box
        component="iframe"
        title="Email preview"
        srcDoc={srcDoc}
        sandbox="allow-same-origin"
        sx={{
          width: '100%',
          minHeight: 380,
          border: 'none',
          display: 'block',
          bgcolor: '#fff',
        }}
      />

      {placeholderCount > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'rgba(25,118,210,0.04)',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Detected <strong>{placeholderCount}</strong> placeholder
            {placeholderCount === 1 ? '' : 's'} (<code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>, …).
            Values will be substituted at send time.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
